import { z } from 'zod';
import { AUDIT_ACTIONS, AUDIT_ENTITIES, IMPORT_SOURCES, IMPORT_STATUSES, REPORT_METRICS, USER_ROLES, } from './enums.js';
import { PERMISSION_KEYS } from './permissions.js';
import { REPORT_METRIC_FIELDS } from './report.js';
import { TREND_STATUSES } from './trend.js';
// ---------- Primitivos ----------
/** YYYY-MM-DD */
export const isoDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
/** YYYY-MM */
export const monthKeySchema = z.string().regex(/^\d{4}-\d{2}$/, 'Mês no formato AAAA-MM');
/** Quantos dias do mês as importações ativas cobrem. Mês parcial = comparação por projeção. */
export const monthCoverageSchema = z.object({
    month: monthKeySchema,
    daysInMonth: z.number().int(),
    daysCovered: z.number().int(),
    partial: z.boolean(),
});
// ---------- Auth ----------
/** Política de senha: 8+ caracteres, com letra e número. Vale para criação, troca e redefinição. */
export const passwordSchema = z
    .string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .max(128, 'A senha deve ter no máximo 128 caracteres')
    .regex(/[A-Za-z]/, 'A senha deve conter pelo menos uma letra')
    .regex(/\d/, 'A senha deve conter pelo menos um número');
export const loginSchema = z.object({
    email: z.string().trim().email('E-mail inválido'),
    password: z.string().min(1, 'Informe a senha'),
});
export const userSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(USER_ROLES),
    active: z.boolean(),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
    lockedUntil: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Usuário da sessão: o que o painel precisa para decidir o que mostrar. */
export const sessionUserSchema = userSchema
    .pick({ id: true, email: true, name: true, role: true, mustChangePassword: true })
    .extend({ permissions: z.array(z.enum(PERMISSION_KEYS)) });
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Informe a senha atual'),
    newPassword: passwordSchema,
});
export const updateProfileSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
});
export const sessionSchema = z.object({
    id: z.string().uuid(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime(),
    current: z.boolean(),
});
// ---------- Users (gestão) ----------
export const userCreateSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: z.string().trim().email('E-mail inválido').max(254),
    password: passwordSchema,
    role: z.enum(USER_ROLES),
    /** Por padrão o usuário troca a senha no primeiro acesso. */
    mustChangePassword: z.boolean().default(true),
});
export const userUpdateSchema = z
    .object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: z.string().trim().email('E-mail inválido').max(254),
    role: z.enum(USER_ROLES),
    active: z.boolean(),
})
    .partial();
export const userResetPasswordSchema = z.object({
    password: passwordSchema,
    mustChangePassword: z.boolean().default(true),
});
export const userListItemSchema = userSchema.extend({ activeSessions: z.number().int() });
// ---------- Roles ----------
export const roleSchema = z.object({
    key: z.enum(USER_ROLES),
    name: z.string(),
    description: z.string(),
    /** `super_admin`: sempre todas as permissões, sem linha editável. */
    fixed: z.boolean(),
    /** Quem consulta não pode editar este perfil (fixo, ou `admin` visto por um não-super). */
    locked: z.boolean(),
    permissions: z.array(z.enum(PERMISSION_KEYS)),
    userCount: z.number().int(),
    updatedAt: z.string().datetime().nullable(),
});
export const rolePermissionsInputSchema = z.object({
    permissions: z.array(z.enum(PERMISSION_KEYS)),
});
// ---------- Audit ----------
export const auditChangeSchema = z.object({ from: z.unknown(), to: z.unknown() });
export const auditLogSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    actorId: z.string().uuid().nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    action: z.enum(AUDIT_ACTIONS),
    entity: z.enum(AUDIT_ENTITIES),
    entityId: z.string().nullable(),
    entityLabel: z.string().nullable(),
    changes: z.record(z.string(), auditChangeSchema).nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    requestId: z.string().nullable(),
});
// ---------- Pagination ----------
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
    sortBy: z.string().optional(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export const paginatedSchema = (item) => z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
export const auditFilterSchema = paginationQuerySchema.extend({
    action: z.enum(AUDIT_ACTIONS).optional(),
    entity: z.enum(AUDIT_ENTITIES).optional(),
    entityId: z.string().optional(),
    actorId: z.string().uuid().optional(),
    /** Busca em nome/e-mail do ator e no rótulo da entidade. */
    q: z.string().trim().max(200).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
// ---------- Métricas de uma linha do relatório ----------
const metricNumbers = Object.fromEntries(REPORT_METRIC_FIELDS.map((f) => [f, z.number()]));
export const reportMetricsSchema = z.object(metricNumbers);
/** Totais que aparecem em listagens e cards. */
export const totalsSchema = z.object({
    grossSales: z.number(),
    netSales: z.number(),
    commissionsNet: z.number(),
    orders: z.number().int(),
    customers: z.number().int(),
    visitors: z.number().int(),
});
// ---------- Importações ----------
export const importSchema = z.object({
    id: z.string().uuid(),
    source: z.enum(IMPORT_SOURCES),
    status: z.enum(IMPORT_STATUSES),
    fileName: z.string(),
    fileSize: z.number().int(),
    fileHash: z.string(),
    title: z.string().nullable(),
    reportDate: isoDaySchema.nullable(),
    periodStart: isoDaySchema,
    periodEnd: isoDaySchema,
    rowCount: z.number().int(),
    affiliateCount: z.number().int(),
    productCount: z.number().int(),
    totals: totalsSchema,
    /** Avisos da leitura (ex.: soma das linhas diferente da linha "Total" do arquivo). */
    warnings: z.array(z.string()),
    createdById: z.string().uuid().nullable(),
    createdByName: z.string().nullable(),
    supersededById: z.string().uuid().nullable(),
    supersededAt: z.string().datetime().nullable(),
    emailFrom: z.string().nullable(),
    emailSubject: z.string().nullable(),
    emailReceivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Importação ativa cujo período cruza com a que está chegando. */
export const importConflictSchema = importSchema.pick({
    id: true,
    source: true,
    status: true,
    fileName: true,
    title: true,
    periodStart: true,
    periodEnd: true,
    rowCount: true,
    totals: true,
    createdAt: true,
});
/** O que a leitura do arquivo encontrou, antes de gravar. */
export const importPreviewSchema = z.object({
    fileName: z.string(),
    fileSize: z.number().int(),
    fileHash: z.string(),
    title: z.string().nullable(),
    reportDate: isoDaySchema.nullable(),
    periodStart: isoDaySchema.nullable(),
    periodEnd: isoDaySchema.nullable(),
    /** O período veio do título/nome do arquivo (`detected`) ou foi informado pelo usuário. */
    periodSource: z.enum(['detected', 'manual', 'missing']),
    rowCount: z.number().int(),
    affiliateCount: z.number().int(),
    productCount: z.number().int(),
    totals: totalsSchema,
    warnings: z.array(z.string()),
    conflicts: z.array(importConflictSchema),
    /** Já existe uma importação ativa com exatamente este arquivo. */
    duplicateOf: importConflictSchema.nullable(),
    /** O período cruza mais de um mês: precisa ser corrigido antes de importar. */
    errors: z.array(z.string()),
});
export const importFilterSchema = paginationQuerySchema.extend({
    status: z.enum(IMPORT_STATUSES).optional(),
    source: z.enum(IMPORT_SOURCES).optional(),
    month: monthKeySchema.optional(),
    q: z.string().trim().max(200).optional(),
});
export const importRowSchema = reportMetricsSchema.extend({
    id: z.string(),
    rowIndex: z.number().int(),
    affiliate: z.object({ id: z.string().uuid(), name: z.string() }),
    product: z.object({ id: z.string().uuid(), name: z.string() }),
    rawAffiliate: z.string(),
    rawAccount: z.string(),
    periodStart: isoDaySchema,
    periodEnd: isoDaySchema,
});
export const importRowFilterSchema = paginationQuerySchema.extend({
    q: z.string().trim().max(200).optional(),
    /** `sales` = só linhas com faturamento bruto diferente de zero. */
    only: z.enum(['all', 'sales']).default('all'),
});
/** Campos extras aceitos junto com o arquivo (multipart). */
export const importUploadFieldsSchema = z.object({
    /** Substitui as importações ativas que cruzam o período. */
    replace: z.boolean().default(false),
    /** Período informado à mão quando o arquivo não o declara (ou para corrigir). */
    periodStart: isoDaySchema.optional(),
    periodEnd: isoDaySchema.optional(),
});
export const importResultSchema = z.object({
    import: importSchema,
    /** Importações que passaram a `superseded` por causa desta. */
    superseded: z.array(importConflictSchema),
    affiliatesCreated: z.number().int(),
    productsCreated: z.number().int(),
});
// ---------- Afiliados ----------
export const affiliateSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    key: z.string(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const affiliateListItemSchema = affiliateSchema.extend({
    totals: totalsSchema,
    firstPeriod: isoDaySchema.nullable(),
    lastPeriod: isoDaySchema.nullable(),
    monthsActive: z.number().int(),
    /** Nomes como vieram nos relatórios e que apontam para este cadastro (além do principal). */
    aliasCount: z.number().int(),
});
export const AFFILIATE_SORTS = ['netSales', 'grossSales', 'orders', 'name', 'lastPeriod'];
export const affiliateFilterSchema = paginationQuerySchema.extend({
    search: z.string().trim().max(200).optional(),
    from: monthKeySchema.optional(),
    to: monthKeySchema.optional(),
    /** `active` = com faturamento bruto no período; `all` inclui quem só teve visitas. */
    only: z.enum(['all', 'active']).default('all'),
    sortBy: z.enum(AFFILIATE_SORTS).default('netSales'),
});
export const affiliateUpdateSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome').max(200).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
});
/** Une o afiliado `sourceId` ao afiliado da rota: linhas e nomes passam para ele. */
export const affiliateMergeSchema = z.object({ sourceId: z.string().uuid() });
export const monthTotalsSchema = totalsSchema.extend({ month: monthKeySchema });
export const affiliateDetailSchema = affiliateListItemSchema.extend({
    aliases: z.array(z.object({ rawName: z.string(), createdAt: z.string().datetime() })),
    byMonth: z.array(monthTotalsSchema),
    /** Cobertura dos meses do intervalo (para a variação mês a mês com projeção). */
    coverage: z.array(monthCoverageSchema),
    byProduct: z.array(totalsSchema.extend({ productId: z.string().uuid(), productName: z.string() })),
    /** Cadastros com nome parecido (só espaços internos diferentes): candidatos a unir. */
    possibleDuplicates: z.array(z.object({ id: z.string().uuid(), name: z.string(), totals: totalsSchema })),
});
// ---------- Produtos ----------
export const productSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    key: z.string(),
    createdAt: z.string().datetime(),
});
export const productListItemSchema = productSchema.extend({
    totals: totalsSchema,
    affiliatesActive: z.number().int(),
    firstPeriod: isoDaySchema.nullable(),
    lastPeriod: isoDaySchema.nullable(),
});
export const productFilterSchema = z.object({
    from: monthKeySchema.optional(),
    to: monthKeySchema.optional(),
    search: z.string().trim().max(200).optional(),
});
// ---------- Relatórios (matrizes) ----------
export const REPORT_GROUPINGS = ['affiliate', 'product'];
/** `trend` = pela variação do último mês contra o anterior (asc = maior queda primeiro). */
export const MATRIX_SORTS = ['netSales', 'grossSales', 'name', 'trend'];
/** Variação contra o período anterior (ver `computeTrend`). */
export const trendSchema = z.object({
    ratio: z.number().nullable(),
    status: z.enum(TREND_STATUSES).nullable(),
});
export const metricTrendSchema = z.object({ gross: trendSchema, net: trendSchema });
/** Célula da matriz: null = nenhuma linha de relatório para aquele período. */
export const matrixCellSchema = z.object({
    gross: z.number().nullable(),
    net: z.number().nullable(),
    /** Variação contra a coluna anterior (só no mensal; no diário fica nula). */
    trend: metricTrendSchema,
});
export const matrixRowSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Posição pelo faturamento líquido total (1 = maior), como no Planilhão. */
    rank: z.number().int(),
    cells: z.array(matrixCellSchema),
    totalGross: z.number(),
    totalNet: z.number(),
    /** Participação no líquido total (0–1). */
    share: z.number(),
    /** Participação acumulada até esta posição (0–1), para a curva ABC. */
    cumulativeShare: z.number(),
    /** Variação da última coluna contra a anterior: a "tendência" do afiliado. */
    lastTrend: metricTrendSchema,
});
export const matrixTotalsSchema = z.object({
    cells: z.array(matrixCellSchema),
    totalGross: z.number(),
    totalNet: z.number(),
    /** Quantas linhas (afiliados/produtos) existem no conjunto, antes da paginação. */
    entities: z.number().int(),
    /** Quantas têm faturamento bruto > 0 no período. */
    entitiesActive: z.number().int(),
});
export const monthlyReportQuerySchema = z.object({
    from: monthKeySchema.optional(),
    to: monthKeySchema.optional(),
    by: z.enum(REPORT_GROUPINGS).default('affiliate'),
    /** Só linhas deste produto (quando `by = affiliate`) ou deste afiliado (quando `by = product`). */
    productId: z.string().uuid().optional(),
    affiliateId: z.string().uuid().optional(),
    search: z.string().trim().max(200).optional(),
    /** `active` esconde quem não faturou nada no intervalo; `alert` só quem caiu ou zerou no último mês. */
    only: z.enum(['all', 'active', 'alert']).default('all'),
    sortBy: z.enum(MATRIX_SORTS).default('netSales'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
    /** Métrica usada no filtro `alert` e na ordenação `trend`. */
    trendMetric: z.enum(REPORT_METRICS).default('net'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
export const monthlyReportSchema = z.object({
    months: z.array(monthKeySchema),
    coverage: z.array(monthCoverageSchema),
    by: z.enum(REPORT_GROUPINGS),
    items: z.array(matrixRowSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    totals: matrixTotalsSchema,
});
export const dailyColumnSchema = z.object({
    key: z.string(),
    label: z.string(),
    start: isoDaySchema,
    end: isoDaySchema,
    /** Quantos dias o período cobre (1 = relatório diário). */
    days: z.number().int(),
});
export const dailyReportQuerySchema = monthlyReportQuerySchema
    .omit({ from: true, to: true })
    .extend({ month: monthKeySchema });
export const dailyReportSchema = z.object({
    month: monthKeySchema,
    by: z.enum(REPORT_GROUPINGS),
    columns: z.array(dailyColumnSchema),
    items: z.array(matrixRowSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    totals: matrixTotalsSchema,
});
export const exportQuerySchema = z.object({
    metric: z.enum([...REPORT_METRICS, 'both']).default('both'),
    /** Pinta as células com a variação (cores do painel). */
    heat: z
        .enum(['0', '1', 'true', 'false'])
        .default('1')
        .transform((v) => v === '1' || v === 'true'),
});
// ---------- Painel ----------
export const dashboardQuerySchema = z.object({
    from: monthKeySchema.optional(),
    to: monthKeySchema.optional(),
});
export const dashboardStatsSchema = z.object({
    availableMonths: z.array(monthKeySchema),
    range: z.object({ from: monthKeySchema, to: monthKeySchema }).nullable(),
    kpis: totalsSchema.extend({
        affiliatesTotal: z.number().int(),
        affiliatesActive: z.number().int(),
        productsActive: z.number().int(),
        imports: z.number().int(),
    }),
    monthly: z.array(monthTotalsSchema.extend({ affiliatesActive: z.number().int() })),
    topAffiliates: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        grossSales: z.number(),
        netSales: z.number(),
        share: z.number(),
    })),
    topProducts: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        grossSales: z.number(),
        netSales: z.number(),
        share: z.number(),
    })),
    /** Curva ABC do líquido: quanto os 10 maiores representam e quantos fazem 80%. */
    pareto: z.object({
        top10Share: z.number(),
        affiliatesTo80: z.number().int(),
    }),
    recentImports: z.array(importSchema),
});
// ---------- Configurações ----------
export const settingsSchema = z.object({
    companyName: z.string().min(1),
    timezone: z.string().min(1),
    // Caixa de e-mail que recebe os relatórios da BuyGoods (IMAP)
    mailboxEnabled: z.boolean(),
    mailboxHost: z.string().nullable(),
    mailboxPort: z.number().int().min(1).max(65535),
    mailboxSecure: z.boolean(),
    mailboxUser: z.string().nullable(),
    mailboxFolder: z.string().min(1),
    /** Só processa mensagens cujo remetente contenha este texto (vazio = qualquer). */
    mailboxSenderFilter: z.string().nullable(),
    /** Só processa mensagens cujo assunto contenha este texto (vazio = qualquer). */
    mailboxSubjectFilter: z.string().nullable(),
    mailboxIntervalMin: z.number().int().min(1).max(1440),
    /** Marca a mensagem como lida depois de processar. */
    mailboxMarkSeen: z.boolean(),
    /** Ignora mensagens anteriores a esta data na primeira leitura. */
    mailboxSince: isoDaySchema.nullable(),
    hasMailboxPassword: z.boolean(),
});
export const settingsInputSchema = settingsSchema
    .omit({ hasMailboxPassword: true })
    .partial()
    .extend({ mailboxPassword: z.string().min(1).optional() });
/** Campos da conexão aceitos pelo "Testar conexão" sem precisar salvar antes. */
export const mailboxConnectionInputSchema = settingsInputSchema.pick({
    mailboxHost: true,
    mailboxPort: true,
    mailboxSecure: true,
    mailboxUser: true,
    mailboxPassword: true,
    mailboxFolder: true,
    mailboxSenderFilter: true,
    mailboxSubjectFilter: true,
    mailboxSince: true,
});
export const mailboxTestSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
    /** Mensagens na pasta que ainda não foram processadas (pelos filtros). */
    pending: z.number().int().nullable(),
    /** Pastas disponíveis na conta, para conferir o nome. */
    folders: z.array(z.string()),
});
export const mailboxStatusSchema = z.object({
    configured: z.boolean(),
    enabled: z.boolean(),
    intervalMin: z.number().int(),
    /** Último UID processado na pasta. */
    cursor: z.string().nullable(),
    lastRunAt: z.string().nullable(),
    lastError: z.string().nullable(),
    /** Relatórios importados pela caixa de e-mail desde sempre. */
    imported: z.number().int(),
    running: z.boolean(),
    queued: z.boolean(),
    /** O worker registrou o agendamento periódico (está no ar). */
    scheduled: z.boolean(),
    nextRunAt: z.string().nullable(),
});
// ---------- Tempo real ----------
export const realtimeEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('import.updated'),
        importId: z.string().uuid(),
        status: z.enum(IMPORT_STATUSES),
        source: z.enum(IMPORT_SOURCES),
    }),
]);
